import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authService } from '@services/auth.service';
import { User, LoginRequest, ApiError } from '../../types';
import { RootState } from '@store/index';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  errorCode?: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  errorCode: null,
};

// Async thunks
export const login = createAsyncThunk<User, LoginRequest, { rejectValue: ApiError }>(
  'auth/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials);
      return response.user;
    } catch (error) {
      return rejectWithValue(error as ApiError);
    }
  }
);

export const logout = createAsyncThunk<void, void, { rejectValue: ApiError }>(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout();
      return;
    } catch (error) {
      return rejectWithValue(error as ApiError);
    }
  }
);

export const fetchCurrentUser = createAsyncThunk<User, void, { rejectValue: ApiError }>(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      return await authService.getCurrentUser();
    } catch (error) {
      return rejectWithValue(error as ApiError);
    }
  }
);

export const validateSession = createAsyncThunk<User | null, void, { rejectValue: ApiError }>(
  'auth/validateSession',
  async (_, { rejectWithValue }) => {
    try {
      return await authService.validateSession();
    } catch (error) {
      return rejectWithValue(error as ApiError);
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.errorCode = null;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
    sessionExpired: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.error = null;
      state.errorCode = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        // State carries the code, never the server's prose: `error` is the
        // presence flag the view keys on, and useLoginForm translates the code.
        state.errorCode = action.payload?.code || null;
        state.error = state.errorCode || 'AUTH_LOGIN_FAILED';
        state.isAuthenticated = false;
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(logout.rejected, (state, action) => {
        // Even if logout fails, clear user state
        state.user = null;
        state.isAuthenticated = false;
        state.errorCode = action.payload?.code || null;
        state.error = state.errorCode || 'AUTH_LOGOUT_FAILED';
      })
      // Fetch current user
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
        state.errorCode = null;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.isLoading = false;
        state.errorCode = action.payload?.code || null;
        state.error = state.errorCode || 'AUTH_FETCH_USER_FAILED';
        state.isAuthenticated = false;
      })
      // Validate session
      .addCase(validateSession.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(validateSession.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload) {
          // Adopting the validated user here is what removes the second
          // /auth/me the bootstrap used to make for the same record.
          state.user = action.payload;
          state.isAuthenticated = true;
        } else {
          state.user = null;
          state.isAuthenticated = false;
        }
      })
      .addCase(validateSession.rejected, (state) => {
        // Transport/dependency failure is indeterminate, not a logout.
        // A bootstrap without a verified user remains unauthenticated.
        state.isLoading = false;
        // Invalid credentials resolve to null above. Rejection means the
        // server could not be checked: preserve an established session, and
        // never create one for an unverified bootstrap.
        state.errorCode = null;
      })
      // Set dummy user for development
      .addCase(setDummyUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.error = null;
        state.errorCode = null;
      });
  },
});

// Development helper action to set a dummy user
export const setDummyUser = createAsyncThunk<User, void>(
  'auth/setDummyUser',
  async () => {
    // Create a dummy user for development
    const dummyUser: User = {
      id: 'dev-user-1',
      email: 'dev@example.com',
      fullName: 'Development User',
      role: 'admin',
      tenantId: 'tenant1',
      tenantCode: 'DEV',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      permissions: [
        'document:view',
        'document:create',
        'document:edit',
        'document:delete',
        'document:share',
        'admin:access',
      ],
    };
    return dummyUser;
  }
);

// Actions
export const { clearError, setUser, sessionExpired } = authSlice.actions;

// Selectors
export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectAuthLoading = (state: RootState) => state.auth.isLoading;
export const selectAuthError = (state: RootState) => state.auth.error;
export const selectAuthErrorCode = (state: RootState) => state.auth.errorCode ?? null;

export default authSlice.reducer;
